import { Box } from 'entities/Box';
import { MapConfig } from '../mapConfigSerialization';

export type PickerBox = Box;

export interface WarehouseMapConstructorSchema {
  selectedFloorId: Nullable<string>;
  activeTool: WarehouseMapTool;
  shapes: Shape[];
  pickedBoxParams: Nullable<PickedBoxParams>;
  snapStepPixels: number;
  showGrid: boolean;
  mapConfig: MapConfig;
  /** Non-null while a polygon or polygon-box shape is in vertex-edit mode. */
  vertexEditShapeId: Nullable<string>;
}

export interface PickedBoxParams {
  id: string;
  label: string;
  width: number;
  length: number;
  sizeCodeId?: string;
}

export interface UpdateShapeParamsPayload {
  x: number;
  y: number;
  width: number;
  height: number;
  radiusX: number;
  radiusY: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  points: number[];
  label: string;
  labelOffsetX: number;
  labelOffsetY: number;
  zIndex: number;
}

export enum ShapeType {
  RECTANGLE = 'RECTANGLE',
  CIRCLE = 'CIRCLE',
  LINE = 'LINE',
  TEXT = 'TEXT',
  POLYGON = 'POLYGON',
  POLYGON_BOX = 'POLYGON_BOX',
}

export interface ShapePlacement {
  x: number;
  y: number;
}

export interface ShapeSize {
  width: number;
  height: number;
}

export interface ShapeScale {
  scaleX: number;
  scaleY: number;
}

export interface ShapeRadius {
  radiusX: number;
  radiusY: number;
}

export interface CommonShape extends ShapePlacement {
  id: string;
  floorId: string;
  type: ShapeType;
  cornerRadius?: number;
  ignoreStroke?: boolean;
  selected?: boolean;
  rotation?: number;
  locked?: boolean;
  zIndex?: number;
}

export interface CommonShapeStyle {
  fill: string;
  stroke: string;
  strokeWidth: number;
}

/** Marker mixin for shapes that render an editable text label. */
export interface LabeledShape {
  label: string;
  fontSize?: number;
  labelRotation?: number;
}

/** Marker mixin for shapes that map to a warehouse box and therefore carry a size code. */
export interface WithSizeCode {
  sizeCodeId?: string;
}

export interface RectangleShape extends CommonShape, ShapeSize, ShapeScale, CommonShapeStyle, LabeledShape, WithSizeCode {
  type: ShapeType.RECTANGLE;
  /** Pixel offset of the label from the default centered position within the shape. */
  labelOffsetX?: number;
  labelOffsetY?: number;
}

/**
 * Renders as a true ellipse via Konva.Ellipse. We keep the ShapeType.CIRCLE
 * enum value to avoid touching tool identifiers, draw-tool strings and
 * persisted floor configs; only the in-memory type is renamed.
 */
export interface EllipseShape extends CommonShape, ShapeRadius, ShapeScale, CommonShapeStyle, LabeledShape, WithSizeCode {
  type: ShapeType.CIRCLE;
}

export interface LineShape extends CommonShape, ShapeScale, CommonShapeStyle {
  type: ShapeType.LINE;
  points: number[];
}

export interface TextShape extends CommonShape, ShapeScale, LabeledShape {
  type: ShapeType.TEXT;
}

export interface PolygonShape extends CommonShape, ShapeScale, CommonShapeStyle {
  type: ShapeType.POLYGON;
  points: number[];
}

export interface PolygonBoxShape extends CommonShape, ShapeScale, CommonShapeStyle, LabeledShape, WithSizeCode {
  type: ShapeType.POLYGON_BOX;
  points: number[];
  /** Pixel offset of the label from the default center of the bounding box. */
  labelOffsetX?: number;
  labelOffsetY?: number;
}

export type Shape = RectangleShape | EllipseShape | LineShape | TextShape | PolygonShape | PolygonBoxShape;

/** Concrete subset of `Shape` that carries an editable label. */
export type LabeledShapeUnion = RectangleShape | EllipseShape | TextShape | PolygonBoxShape;

/** Concrete subset of `Shape` that maps to a warehouse box (and therefore a size code). */
export type BoxShape = RectangleShape | EllipseShape | PolygonBoxShape;

/**
 * Runtime narrowing helpers — kept in lockstep with the structural mixins
 * above so a single source of truth governs both the type-system view and
 * the runtime guards used by reducers, validators and UI conditionals.
 */
export const isLabeledShape = (s: Shape): s is LabeledShapeUnion =>
  s.type === ShapeType.RECTANGLE || s.type === ShapeType.CIRCLE || s.type === ShapeType.TEXT || s.type === ShapeType.POLYGON_BOX;

export const isBoxShape = (s: Shape): s is BoxShape =>
  s.type === ShapeType.RECTANGLE || s.type === ShapeType.CIRCLE || s.type === ShapeType.POLYGON_BOX;

export const SHAPE_COMPARABLE_FIELDS: Record<ShapeType, ReadonlyArray<keyof Shape | string>> = {
  [ShapeType.RECTANGLE]: [
    'x',
    'y',
    'width',
    'height',
    'rotation',
    'label',
    'stroke',
    'strokeWidth',
    'fill',
    'cornerRadius',
    'sizeCodeId',
    'locked',
    'zIndex',
    'labelOffsetX',
    'labelOffsetY',
    'fontSize',
    'labelRotation',
  ],
  [ShapeType.CIRCLE]: [
    'x',
    'y',
    'radiusX',
    'radiusY',
    'rotation',
    'label',
    'stroke',
    'strokeWidth',
    'fill',
    'sizeCodeId',
    'locked',
    'zIndex',
    'fontSize',
    'labelRotation',
  ],
  [ShapeType.LINE]: ['x', 'y', 'points', 'stroke', 'strokeWidth', 'locked', 'zIndex'],
  [ShapeType.TEXT]: ['x', 'y', 'label', 'fontSize', 'rotation', 'locked', 'zIndex'],
  [ShapeType.POLYGON]: ['x', 'y', 'points', 'stroke', 'strokeWidth', 'rotation', 'locked', 'zIndex'],
  [ShapeType.POLYGON_BOX]: [
    'x',
    'y',
    'points',
    'rotation',
    'label',
    'stroke',
    'strokeWidth',
    'fill',
    'sizeCodeId',
    'locked',
    'zIndex',
    'labelOffsetX',
    'labelOffsetY',
    'fontSize',
    'labelRotation',
  ],
};

export enum WarehouseMapTool {
  POINTER = 'POINTER',
  GRAB = 'GRAB',
  EDIT_BACKGROUND = 'EDIT_BACKGROUND',
  RECTANGLE = 'RECTANGLE',
  CIRCLE = 'CIRCLE',
  PENCIL = 'PENCIL',
  LINE = 'LINE',
  TEXT = 'TEXT',
  POLYGON = 'POLYGON',
  POLYGON_BOX = 'POLYGON_BOX',
}

export type SelectionBox = ShapePlacement & ShapeSize;
